import { PeriodType } from "./payment";

export type PauseSubscriptionRequest = { months: 1 | 2 | 3 };

export type PauseSubscriptionResponse = {
    pauseUntil: string;
};

export const isSolidgateSubscriptionPauseEligible = (subscription: ISubscription): boolean => {
    if (subscription.platform.code !== "solidgate") return false;
    if (!subscription.isAutoRenewable) return false;

    return new Date(subscription.endDate).getTime() > Date.now();
};

export interface ISubscriptionPlatform {
    id: number;
    code: string;
}

export interface ISubscriptionPlan {
    id: number;
    planCode: string;
    planText: string;
    oldPrice: number;
    newPrice: number;
    period: number;
    periodType: PeriodType;
    currency: string;
}

export interface ISubscription {
    id: number;
    subscriptionId: string;
    platform: ISubscriptionPlatform;
    plan: ISubscriptionPlan;
    startDate: string;
    endDate: string;
    isAutoRenewable: boolean;
    renewCount: number;
    createdAt: string;
    updatedAt: string;
    price: number;
    currency: string;
    country?: string;
    pauseFrom?: string | null;
    pauseTo?: string | null;
}

export const isSubscriptionPaused = (subscription: ISubscription, studentStatus?: string | null): boolean => {
    if (subscription.platform.code !== "solidgate" || subscription.isAutoRenewable) return false;

    // Per-subscription pause dates stored from the Solidgate pause webhook.
    // Unlike endDate, pauseTo stays valid when the paid period ends during the
    // pause: billing is deferred, not expired, so the subscription is still
    // paused until pauseTo.
    if (subscription.pauseTo) {
        return new Date(subscription.pauseTo).getTime() > Date.now();
    }

    // Fallback for subscriptions paused before pause dates were stored:
    // student.status is set by the pause webhook, endDate is the best guess.
    return studentStatus === "paused" && new Date(subscription.endDate).getTime() > Date.now();
};

export type PurchaseUpsellResponse = {
    order_id: string;
    product_id: string;
    currency: string;
    type: string;
    payment_type: string;
    customer_account_id: string;
    customer_email: string;
    ip_address: string;
    order_description: string;
    platform: string;
    status?: string;
    verify_url?: string;
};

export type PurchaseUpsellRequest = {
    productId: string;
    currency: string;
    platform: "WEB" | "MOB";
    purchasePageURL: string;
    placement: UpsellPlacement;
    successUrl: string;
    failUrl: string;
};

export type PurchaseUpsellAppleResponse = {
    success: boolean;
    productId: string;
    paymentId: string;
};

export interface ProductStatus {
    orderStatus: string;
    transactionStatus: string;
    paymentId?: string;
    verifyUrl?: string;
}

export interface CheckPurchasedProductsResponse {
    purchasedProducts: Record<string, boolean>;
    productStatus: Record<string, ProductStatus>;
}

export enum UpsellPlacement {
    FirstLogin = "first_login",
    PromptsLibrary = "prompts_library",
    Link = "link",
    Assistant = "assistant",
    Bundle = "bundle",
    Lesson = "lesson"
}
